import { useEffect, useState } from 'react';
import { Search, Plus, Eye, Truck, Package, User, Calendar, CheckCircle, Printer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

export default function DrsPage() {
  const navigate = useNavigate();
  const [drs, setDrs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, outForDelivery: 0, delivered: 0, returned: 0 });

  useEffect(() => { fetchDrs(); }, []);

  const fetchDrs = async () => {
    try {
      const response = await api.get('/api/drs');
      const data = response.data.data || response.data.items || response.data || [];
      const items = Array.isArray(data) ? data : (data.items || []);
      setDrs(items);
      setStats({
        total: items.length,
        outForDelivery: items.filter(d => (d.status || '').toLowerCase().includes('out')).length,
        delivered: items.filter(d => (d.status || '').toLowerCase().includes('delivered')).length,
        returned: items.filter(d => (d.status || '').toLowerCase().includes('return')).length
      });
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('out')) return 'bg-blue-100 text-blue-800';
    if (s.includes('delivered')) return 'bg-green-100 text-green-800';
    if (s.includes('return')) return 'bg-orange-100 text-orange-800';
    if (s.includes('pending')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr: string) => dateStr ? new Date(dateStr).toLocaleString('ar-EG') : '-';

  const filteredDrs = drs.filter(d => {
    const matchesSearch = (d.drsNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (d.delegateName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || (d.status || '').toLowerCase().includes(statusFilter);
    return matchesSearch && matchesStatus;
  });

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      {/* Header مع الشعار */}
      <div className="border-b-2 border-gray-300 bg-white print:border-b-2">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-right">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Target Express</h1>
              <p className="text-sm text-gray-600">بيانات التسليم (DR)</p>
              <p className="text-xs text-gray-500 mt-1">Shipping, Logistics & Fulfillment Solutions</p>
            </div>
            <div className="flex items-center gap-3">
              <img src="/logo-waigh-transparent.png" alt="Target Express" className="h-24 w-auto object-contain" />
            </div>
          </div>
        </div>
      </div>

      {/* محتوى الصفحة */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* معلومات الـ DR */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div>
            <p className="text-xs text-gray-600 mb-1">إجمالي DR</p>
            <p className="font-bold text-lg text-orange-600">{stats.total}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 mb-1">قيد التوصيل</p>
            <p className="font-bold text-lg text-blue-700">{stats.outForDelivery}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 mb-1">تم التسليم</p>
            <p className="font-bold text-lg text-green-700">{stats.delivered}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 mb-1">مرتجع</p>
            <p className="font-bold text-lg text-orange-700">{stats.returned}</p>
          </div>
        </div>

        {/* شريط البحث والفلترة */}
        <div className="flex gap-4 flex-wrap mb-6 no-print">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="بحث..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 pl-4 py-2 border rounded-lg"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">جميع الحالات</option>
            <option value="out">قيد التوصيل</option>
            <option value="delivered">تم التسليم</option>
            <option value="return">مرتجع</option>
          </select>
          <Button onClick={handlePrint} className="bg-orange-600 hover:bg-orange-700">
            <Printer className="w-4 h-4 ml-2" /> طباعة
          </Button>
          <Button onClick={() => navigate('/drs/new')} className="bg-[#1e40af] hover:bg-[#1e3a8a]">
            <Plus className="w-4 h-4 ml-2" /> DR جديد
          </Button>
        </div>

        {/* جدول DR */}
        <div className="border border-gray-300 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th className="text-right p-3 font-bold text-gray-900">#</th>
                <th className="text-right p-3 font-bold text-gray-900">رقم DR</th>
                <th className="text-right p-3 font-bold text-gray-900">المندوب</th>
                <th className="text-right p-3 font-bold text-gray-900">عدد الشحنات</th>
                <th className="text-right p-3 font-bold text-gray-900">الحالة</th>
                <th className="text-right p-3 font-bold text-gray-900">التاريخ</th>
                <th className="text-right p-3 font-bold text-gray-900 no-print">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
                    <p>جاري التحميل...</p>
                  </td>
                </tr>
              ) : filteredDrs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">لا توجد ايصالات تسليم</p>
                  </td>
                </tr>
              ) : (
                filteredDrs.map((d, idx) => (
                  <tr key={d.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-bold">{idx + 1}</td>
                    <td className="p-3 font-mono font-bold text-orange-700">{d.drsNumber || '-'}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold">{d.delegateName || '-'}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Badge variant="outline">{d.shipmentCount || d.totalShipments || 0} شحنة</Badge>
                    </td>
                    <td className="p-3">
                      <Badge className={getStatusColor(d.status)}>{d.status || '-'}</Badge>
                    </td>
                    <td className="p-3 text-sm text-gray-600">{formatDate(d.createdAt)}</td>
                    <td className="p-3 no-print">
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/drs/${d.id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-gray-300 pt-4 mt-8">
          <div className="flex justify-between items-center text-xs text-gray-600">
            <div>
              <p className="font-semibold">Target Express</p>
              <p>شحن ولوجستيات</p>
            </div>
            <div className="text-center">
              <p>تاريخ الطباعة: {new Date().toLocaleString('ar-EG')}</p>
            </div>
            <div className="text-left">
              <p>تم إنشاء هذا المستند إلكترونياً</p>
              <p>© 2026 Target Express</p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS للطباعة */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          @page { margin: 1cm; }
        }
      `}</style>
    </div>
  );
}
